import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const codeBlockSx = {
  backgroundColor: '#1a1a2e',
  fontFamily: '"Fira Code", "Fira Mono", "Roboto Mono", monospace',
  fontSize: '0.85rem',
  lineHeight: 1.6,
  padding: 2.5,
  borderRadius: 2,
  overflowX: 'auto',
  color: '#E5E7EB',
  whiteSpace: 'pre',
  margin: 0,
} as const;

const paramTableHeaderSx = {
  fontWeight: 600,
  color: '#9CA3AF',
  fontSize: '0.8rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  pb: 0.5,
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const paramRowSx = {
  display: 'grid',
  gridTemplateColumns: '140px 100px 1fr',
  gap: 2,
  py: 1,
  borderBottom: '1px solid rgba(255,255,255,0.04)',
  alignItems: 'baseline',
};

interface ParamRow {
  name: string;
  required: boolean;
  description: string;
}

function ParamsTable({ params }: { params: ParamRow[] }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ ...paramRowSx, pb: 0.5 }}>
        <Typography sx={paramTableHeaderSx}>Parameter</Typography>
        <Typography sx={paramTableHeaderSx}>Required</Typography>
        <Typography sx={paramTableHeaderSx}>Description</Typography>
      </Box>
      {params.map((p) => (
        <Box key={p.name} sx={paramRowSx}>
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              color: '#6C8EFF',
            }}
          >
            {p.name}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem' }}>
            {p.required ? (
              <Chip label="required" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
            ) : (
              <Chip label="optional" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#9CA3AF' }}>
            {p.description}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <Box component="pre" sx={codeBlockSx}>
      {children}
    </Box>
  );
}

const STATS_PARAMS: ParamRow[] = [
  { name: 'from', required: true, description: 'Start date (YYYY-MM-DD)' },
  { name: 'to', required: true, description: 'End date (YYYY-MM-DD)' },
  { name: 'group_by', required: false, description: 'Group results by: date, country, or channel_id. Defaults to date.' },
];

const SNAPSHOT_PARAMS: ParamRow[] = [
  { name: 'from', required: true, description: 'Start date (YYYY-MM-DD)' },
  { name: 'to', required: true, description: 'End date (YYYY-MM-DD)' },
];

const CONVERSIONS_PARAMS: ParamRow[] = [
  { name: 'from', required: true, description: 'Start date (YYYY-MM-DD)' },
  { name: 'to', required: true, description: 'End date (YYYY-MM-DD)' },
  { name: 'limit', required: false, description: 'Number of results to return. Defaults to 100, max 1000.' },
  { name: 'offset', required: false, description: 'Offset for pagination. Defaults to 0.' },
];

const REPORTS_PARAMS: ParamRow[] = [
  { name: 'from', required: true, description: 'Start date (YYYY-MM-DD)' },
  { name: 'to', required: true, description: 'End date (YYYY-MM-DD)' },
  { name: 'group_by', required: false, description: 'Additional grouping: country, flow_id, or both (comma-separated). Date is always included.' },
];

const STATS_RESPONSE = `{
  "rows": [
    {
      "group_key": "2025-06-01",
      "clicks": 1250,
      "conversions": 48,
      "payout": 720.00,
      "cr": 3.84,
      "epc": 0.58
    }
  ]
}`;

const SNAPSHOT_RESPONSE = `{
  "clicks": 8500,
  "conversions": 340,
  "payout": 5100.00,
  "changes": {
    "clicks": 18.06,
    "conversions": 17.24,
    "payout": 17.24
  }
}`;

const CONVERSIONS_RESPONSE = `{
  "rows": [
    {
      "id": "cv_abc123",
      "click_id": "ck_xyz789",
      "country": "US",
      "channel": "My Channel",
      "event_type": "conversion",
      "payout": 15.00,
      "status": "approved",
      "transaction_id": "txn_001",
      "external_id": null,
      "converted_at": "2025-06-01T14:32:00Z"
    }
  ],
  "total": 340
}`;

const REPORTS_RESPONSE = `{
  "rows": [
    {
      "date": "2025-06-01",
      "clicks": 1250,
      "conversions": 48,
      "payout": 720.00,
      "locked_at": "2025-06-02T06:00:00Z"
    }
  ]
}`;

const CURL_EXAMPLE = `curl -X GET "https://api.example.com/partner/stats?from=2025-06-01&to=2025-06-07&group_by=date" \\
  -H "X-Api-Key: YOUR_API_KEY"`;

const JS_EXAMPLE = `const response = await fetch(
  "https://api.example.com/partner/stats?from=2025-06-01&to=2025-06-07&group_by=date",
  {
    headers: {
      "X-Api-Key": "YOUR_API_KEY",
    },
  }
);

const data = await response.json();
console.log(data.rows);`;

const PYTHON_EXAMPLE = `import requests

response = requests.get(
    "https://api.example.com/partner/stats",
    params={
        "from": "2025-06-01",
        "to": "2025-06-07",
        "group_by": "date",
    },
    headers={
        "X-Api-Key": "YOUR_API_KEY",
    },
)

data = response.json()
print(data["rows"])`;

interface EndpointAccordionProps {
  method: string;
  path: string;
  description: string;
  params: ParamRow[];
  responseExample: string;
  defaultExpanded?: boolean;
}

function EndpointAccordion({
  method,
  path,
  description,
  params,
  responseExample,
  defaultExpanded,
}: EndpointAccordionProps) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        backgroundColor: '#111827',
        border: '1px solid rgba(255,255,255,0.06)',
        '&:before': { display: 'none' },
        borderRadius: '12px !important',
        mb: 2,
        '&.Mui-expanded': { mb: 2 },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            label={method}
            size="small"
            sx={{
              backgroundColor: '#00E5A0',
              color: '#0B1120',
              fontWeight: 700,
              fontSize: '0.75rem',
              height: 24,
              fontFamily: 'monospace',
            }}
          />
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.95rem',
              fontWeight: 600,
            }}
          >
            {path}
          </Typography>
          <Typography
            sx={{ color: '#9CA3AF', fontSize: '0.85rem', ml: 1 }}
          >
            {description}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Query Parameters
        </Typography>
        <ParamsTable params={params} />

        <Typography variant="h6" sx={{ mt: 3, mb: 1.5 }}>
          Response Example
        </Typography>
        <CodeBlock>{responseExample}</CodeBlock>
      </AccordionDetails>
    </Accordion>
  );
}

const ApiDocsPage: React.FC = () => {
  const [codeTab, setCodeTab] = useState(0);

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        API Documentation
      </Typography>

      {/* Authentication Section */}
      <Typography variant="h5" sx={{ mb: 2 }}>
        Authentication
      </Typography>
      <Paper
        sx={{
          p: 3,
          mb: 4,
          backgroundColor: '#111827',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Typography sx={{ mb: 2, color: '#E5E7EB' }}>
          All API requests must be authenticated using your API key. Include the
          key in the <code style={{ color: '#6C8EFF' }}>X-Api-Key</code> header
          with every request.
        </Typography>
        <Typography sx={{ mb: 2, color: '#9CA3AF', fontSize: '0.9rem' }}>
          You can find your API key in your account settings. Keep it secret and
          never expose it in client-side code.
        </Typography>
        <CodeBlock>
          {`GET /partner/stats HTTP/1.1
Host: api.example.com
X-Api-Key: YOUR_API_KEY`}
        </CodeBlock>
      </Paper>

      {/* Endpoints Section */}
      <Typography variant="h5" sx={{ mb: 2 }}>
        Endpoints
      </Typography>

      <EndpointAccordion
        method="GET"
        path="/partner/stats"
        description="Aggregated performance data"
        params={STATS_PARAMS}
        responseExample={STATS_RESPONSE}
        defaultExpanded
      />

      <EndpointAccordion
        method="GET"
        path="/partner/stats/summary"
        description="KPI summary with period comparison"
        params={SNAPSHOT_PARAMS}
        responseExample={SNAPSHOT_RESPONSE}
      />

      <EndpointAccordion
        method="GET"
        path="/partner/conversions"
        description="Individual conversion events"
        params={CONVERSIONS_PARAMS}
        responseExample={CONVERSIONS_RESPONSE}
      />

      <EndpointAccordion
        method="GET"
        path="/partner/reports"
        description="Daily locked reports"
        params={REPORTS_PARAMS}
        responseExample={REPORTS_RESPONSE}
      />

      {/* Code Examples Section */}
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Code Examples
      </Typography>
      <Paper
        sx={{
          backgroundColor: '#111827',
          border: '1px solid rgba(255,255,255,0.06)',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={codeTab}
          onChange={(_, v) => setCodeTab(v)}
          sx={{
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            px: 1,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 48,
            },
          }}
        >
          <Tab label="cURL" />
          <Tab label="JavaScript" />
          <Tab label="Python" />
        </Tabs>
        <Box sx={{ p: 0 }}>
          {codeTab === 0 && <CodeBlock>{CURL_EXAMPLE}</CodeBlock>}
          {codeTab === 1 && <CodeBlock>{JS_EXAMPLE}</CodeBlock>}
          {codeTab === 2 && <CodeBlock>{PYTHON_EXAMPLE}</CodeBlock>}
        </Box>
      </Paper>
    </Box>
  );
};

export default ApiDocsPage;
